/**
 * THE SHEET'S SECOND FACE: the same words, in type the browser owns.
 *
 * -- WHY THERE IS ONE SUBTREE AND NOT TWO --------------------------------------
 *
 * Two things were needed and they turned out to be the same thing. A player who cannot
 * see the screen needs the chart's words in the accessibility tree. A player who can see
 * but cannot read 5-pixel glyphs needs those same words at a size their browser can
 * scale. Writing them twice - once hidden for the reader, once visible for the eye -
 * would be two surfaces that drift, which is the failure `hudCopy` was built to stop one
 * layer down.
 *
 * So there is ONE `#say` subtree, and CSS decides what it looks like. Shut, it is the
 * standard clip-rect hide: present in the accessibility tree, zero pixels on screen,
 * nothing over the picture. Open (`body.reading`), the same nodes become a full page of
 * the chart's own paper and ink at 1rem, and the canvas is hidden while it shows. A
 * screen reader and a magnifier read the identical text nodes.
 *
 * -- WHY IT REPLACES THE PICTURE RATHER THAN LYING OVER IT ---------------------
 *
 * world.html's own doctrine, in its own words about the too-small notice: browser text is
 * anti-aliased, "no such text is ever laid over the pixel art. It is only on screen when
 * the pixel art is not." CONTRACT 1.5e rules three legal grains and forbids a fractional
 * scale, so the picture cannot be made to grow with a text size; the resolution is that
 * the two are never on screen together. The pixel art is never scaled fractionally and
 * the scalable text is never over the pixel art.
 *
 * -- WHAT IT COSTS AT IDLE ------------------------------------------------------
 *
 * `render` is called from ONE place: the branch in `world-entry.ts` that already decided
 * the chart's bitmap must be repainted, keyed on `chartKey`. A still chart on a drifting
 * boat never enters that branch, so this costs nothing per step. Inside it, every write
 * is compared first and skipped when the string is unchanged - which matters beyond
 * tidiness for `#say-now`, an `aria-live` region: rewriting a live region with the text
 * it already holds makes some screen readers announce it again, so the dissolve of an
 * aside (which moves `chartKey` about sixteen times as it fades) must not re-speak the
 * sentence sixteen times.
 */

import type { Speech, SpokenRow } from './speech';

/** The nodes `render` owns. Everything else on the page is somebody else's. */
export interface ReadingNodes {
  /** The standing lines, one paragraph each. */
  readonly lines: HTMLElement;
  /** The transient line. An `aria-live="polite"` region; written only when it changes. */
  readonly aside: HTMLElement;
  /** The leaf's heading, and the label of the listbox. */
  readonly heading: HTMLElement;
  /** `role="listbox"`. Its options are the ledger rows. */
  readonly list: HTMLElement;
  /** What the keys do, one paragraph each. */
  readonly keys: HTMLElement;
}

export interface ReadingSurface {
  /** `visible` is whether the pane is the thing on screen, which is all it is used for. */
  render(speech: Speech, visible: boolean): void;
}

/** Write only when it would change something, so a live region is not re-announced. */
function setText(el: HTMLElement, text: string): boolean {
  if (el.textContent === text) return false;
  el.textContent = text;
  return true;
}

/** One paragraph per line, reusing the nodes already there. */
function setLines(host: HTMLElement, lines: readonly string[]): void {
  while (host.childElementCount > lines.length) host.lastElementChild?.remove();
  while (host.childElementCount < lines.length) host.append(document.createElement('p'));
  const kids = host.children;
  for (let i = 0; i < lines.length; i++) setText(kids[i] as HTMLElement, lines[i] ?? '');
}

const ROW_ID = (i: number): string => `say-row-${i}`;

/**
 * ONE OPTION PER ROW, and the row in force carries a MARK rather than only a colour.
 *
 * CONTRACT section 6: never colour alone as a signal. The sheet marks the row in force
 * with the ledger's own blot in the gutter; this carries the same blot, drawn by CSS so
 * it needs no font, and the row's sentence says it in words as well - `speech.ts` writes
 * "being fished for" or "the boat is lying here" into the text itself. Three channels:
 * shape, words, and `aria-selected` for the reader.
 *
 * The sentence is NOT split into a name column and a figure column. The sheet sets the
 * ledger in two columns and could be transcribed that way, but a second structured
 * representation of a row is a second thing to keep in step with the painter, and the
 * screen reader needs the one sentence regardless.
 */
function fillRow(li: HTMLElement, row: SpokenRow): void {
  let blot = li.firstElementChild as HTMLElement | null;
  let text = li.lastElementChild as HTMLElement | null;
  if (blot === null || text === null || blot === text) {
    li.textContent = '';
    blot = document.createElement('span');
    blot.className = 'blot';
    blot.setAttribute('aria-hidden', 'true');
    text = document.createElement('span');
    li.append(blot, text);
  }
  setText(text, row.text);
  li.classList.toggle('marked', row.marked);
  li.classList.toggle('locked', !row.reachable);
  li.setAttribute('aria-selected', String(row.marked));
  if (row.reachable) li.removeAttribute('aria-disabled');
  else li.setAttribute('aria-disabled', 'true');
}

export function makeReading(nodes: ReadingNodes): ReadingSurface {
  let lastCursor = -1;
  return {
    render(speech: Speech, visible: boolean): void {
      setLines(nodes.lines, speech.lines);
      setText(nodes.aside, speech.aside);
      setText(nodes.heading, speech.heading);
      setLines(nodes.keys, speech.keys);

      const list = nodes.list;
      list.hidden = !speech.open;
      nodes.heading.hidden = !speech.open;
      while (list.childElementCount > speech.rows.length) list.lastElementChild?.remove();
      while (list.childElementCount < speech.rows.length) {
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.id = ROW_ID(list.childElementCount);
        list.append(li);
      }
      for (let i = 0; i < speech.rows.length; i++) {
        const row = speech.rows[i];
        if (row !== undefined) fillRow(list.children[i] as HTMLElement, row);
      }

      // THE HAND, as the reader's own cursor. `aria-activedescendant` is how a listbox
      // says "the focus is on this option" without moving the browser's focus off the
      // list - which is the same shape the sheet already has, where the hand moves and
      // the page does not.
      const at = speech.cursor;
      if (at >= 0) list.setAttribute('aria-activedescendant', ROW_ID(at));
      else list.removeAttribute('aria-activedescendant');
      for (let i = 0; i < list.childElementCount; i++) {
        (list.children[i] as HTMLElement).classList.toggle('at', i === at);
      }
      // Only while the pane is the thing on screen: scrolling a clipped 1x1 box does
      // nothing useful and can move the page under a reader who is not looking at it.
      if (visible && at >= 0 && at !== lastCursor) {
        (list.children[at] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
      }
      lastCursor = at;
    },
  };
}
